/** biome-ignore-all lint/suspicious/noExplicitAny: allow any */
import React, {createContext, ReactNode, useCallback, useContext, useMemo, useState} from 'react';
import {CardEnvelopeTemplatePosition} from '../components/CardEnvelopeTemplate/CardEnvelopeTemplate';

export interface EnvelopeProps {
  id?: string;
  type: string;
  onLoad?: () => void;
  redirectTo?: string;
  message?: string;
  className?: string;
  showLiner?: boolean;
  showStickers?: boolean;
  showStamp?: boolean;
  style?: React.CSSProperties;
  parentNode?: string;
  width?: number;
  height?: number;
}

export interface CardProps {
  className?: string;
  fabricArt?: any;
  imageRef?: React.RefObject<HTMLImageElement>;
  redirectTo?: string;
  isFree?: boolean;
  parentNode?: string;
}

interface EnvelopeContextType {
  showEnvelopeFront: boolean;
  handleFlipEnvelope: (isFrontSide: boolean) => void;
  envelopeProps: EnvelopeProps;
  setEnvelopeProps: (props: EnvelopeProps) => void;
  cardProps: Partial<CardProps>;
  setCardProps: (props: Partial<CardProps>) => void;
  position: CardEnvelopeTemplatePosition;
  setPosition: (position: CardEnvelopeTemplatePosition) => void;
}

interface EnvelopeProviderProps {
  children: ReactNode;
}

export const EnvelopeContext = createContext<EnvelopeContextType | null>(null);

export const EnvelopeProvider: React.FC<EnvelopeProviderProps> = ({children}) => {
  const [showEnvelopeFront, setShowEnvelopeFront] = useState<boolean>(false);
  const [envelopeProps, setEnvelopeProps] = useState<EnvelopeProps>({type: 'blank'});
  const [cardProps, setCardProps] = useState<Partial<CardProps>>({});
  const [position, setPosition] = useState<CardEnvelopeTemplatePosition>(
    CardEnvelopeTemplatePosition.BEHIND_CARD
  );

  const handleFlipEnvelope = useCallback((isFrontSide: boolean) => {
    setShowEnvelopeFront(isFrontSide);
  }, []);

  const contextValue = useMemo<EnvelopeContextType>(
    () => ({
      showEnvelopeFront,
      handleFlipEnvelope,
      envelopeProps,
      setEnvelopeProps,
      cardProps,
      setCardProps,
      position,
      setPosition,
    }),
    [showEnvelopeFront, handleFlipEnvelope, envelopeProps, cardProps, position]
  );

  return <EnvelopeContext.Provider value={contextValue}>{children}</EnvelopeContext.Provider>;
};

export const useEnvelopeContext = (): EnvelopeContextType => {
  const context = useContext(EnvelopeContext);
  if (!context) {
    throw new Error('useEnvelopeContext must be used within an EnvelopeProvider');
  }
  return context;
};
